import { IpaType, getFeaturesForOne } from "../phonemes/ipa";
import { Rule } from "../rules/rule";
import { closestConsonant } from "./closestConsonant";
import { closestVowel } from "./closestVowel";
import { consonantReplacements, vowelReplacements } from "./replacements";
import { rulesFromAllophones } from "./rulesFromAllophones";
import { TransmogrificationResults } from "./transmogrificationResults";
import { tryOptionalReplacements } from "./tryOptionalReplacements";

/**
 * Generate rules that transform source phonemes into target phonemes.
 * It uses common phoneme changing rules, "phoneme distance", and common allophones
 * to try to guess at how someone might approximate the source using the target.
 * It also reports on any phonemes not impacted by rules.
 */
export function makeTransmogrification(
  sourceIpa: IpaType[], sourceSegments: IpaType[],
  targetIpa: IpaType[], targetSegments: IpaType[],
  segmentToAllophones: Record<string, string>
): TransmogrificationResults {
  // ɝ & ɚ don't have allophones, so instead try /ɜɹ/ & /əɹ/
  const rule1 = tryAlternate('ɝ', ['ɜ', 'ɹ'], sourceSegments, targetIpa, targetSegments, segmentToAllophones);
  [sourceIpa, sourceSegments] = removeHandled(rule1, sourceIpa, sourceSegments);
  const rule2 = tryAlternate('ɚ', ['ə', 'ɹ'], sourceSegments, targetIpa, targetSegments, segmentToAllophones);
  [sourceIpa, sourceSegments] = removeHandled(rule2, sourceIpa, sourceSegments);

  // see if 'target' contains any allophones for stuff in 'source'
  const { rules: rules1, common } = rulesFromAllophones(sourceSegments, targetSegments, segmentToAllophones);
  [sourceIpa, sourceSegments] = removeHandled(rules1, sourceIpa, sourceSegments);

  // for remaining segments, try common rules + "phoneme distance"
  const { rules: rules2, common: common2, unhandled } = tryDistanceAndReplacements(sourceIpa, sourceSegments, targetIpa, targetSegments);

  const rules = [...rules1, ...rule1, ...rule2, ...rules2]
    // rules with the longest 'from' should come first so the shorter rules don't stomp on them
    .sort((r1, r2) => r2.from.length - r1.from.length);

  return {
    rules,
    common: common.concat(common2),
    unhandled
  }
}

/** e.g., try /ɜɹ/, but turn it into a rule that takes /ɝ/ as input */
function tryAlternate(segment: IpaType, alt: IpaType[],
  sourceSegments: IpaType[],
  targetIpa: IpaType[], targetSegments: IpaType[],
  segmentToAllophones: Record<string, string>
): Rule[] {
  if (sourceSegments.includes(segment) && !targetSegments.includes(segment)) {
    const { rules: other } = makeTransmogrification([], alt, targetIpa, targetSegments, segmentToAllophones);
    if (other.length === 2) {
      return [{ from: segment, to: other[0].to + other[1].to }];
    }
  }
  return [];
}

/** remove anything that's handled by the given rules */
function removeHandled(rules: Rule[], sourceIpa: IpaType[], sourceSegments: IpaType[]): [IpaType[], IpaType[]] {
  const handled = rules.map(r => r.from);
  sourceIpa = sourceIpa.filter(ipa => !handled.includes(ipa));
  sourceSegments = sourceSegments.filter(seg => !handled.includes(seg));
  return [sourceIpa, sourceSegments];
}

function tryDistanceAndReplacements(
  sourceIpa: IpaType[], sourceSegments: IpaType[],
  targetIpa: IpaType[], targetSegments: IpaType[],
): TransmogrificationResults {
  const common = sourceIpa.filter(p => targetIpa.includes(p));
  const inToCheck = sourceIpa.filter(p => !targetIpa.includes(p));
  const inVowels = inToCheck.filter(p => getFeaturesForOne(p).includes('vowel'));
  const inConsonants = inToCheck.filter(p => getFeaturesForOne(p).includes('consonant') && !getFeaturesForOne(p).includes('affricate'));
  const inAffricates = sourceSegments.filter(p => getFeaturesForOne(p).includes('affricate'));

  const outVowels = targetIpa.filter(p => getFeaturesForOne(p).includes('vowel'));
  const outAffricates = targetSegments.filter(p => getFeaturesForOne(p).includes('affricate'));
  const outConsonants = targetIpa.filter(p => getFeaturesForOne(p).includes('consonant')).concat(outAffricates);

  const rules: Rule[] = [];
  const unhandled = inToCheck.filter(p => !inVowels.includes(p) && !inConsonants.includes(p)
    && p !== 't̠ʃ' && p !== 'd̠ʒ'); // these two are handled as affricates w/out the diacritic

  // preserve common multi-symbol segments before rules for single symbols
  const commonSegments = sourceSegments.filter(p => p.length > 1 && targetSegments.includes(p));
  commonSegments.forEach(s => rules.push({ from: s, to: s }));

  inVowels.forEach(v => {
    const result1 = tryOptionalReplacements(v, outVowels, vowelReplacements);
    if (result1 !== null) {
      rules.push({ from: v, to: result1.match });
    } else {
      const result2 = closestVowel(v, outVowels);
      if (result2 === null) {
        unhandled.push(v);
      } else {
        rules.push({ from: v, to: result2 });
      }
    }
  });

  // do affricates before other consonants because they have multiple symbols,
  // some of which may get transformed below
  inAffricates.forEach(a => {
    if (!commonSegments.includes(a)) {
      const result = closestConsonant(a, outConsonants);
      if (result === null) {
        unhandled.push(a);
      } else {
        rules.push({ from: a, to: result });
      }
    }
  });

  inConsonants.forEach(c => {
    const result1 = tryOptionalReplacements(c, outConsonants, consonantReplacements);
    if (result1 !== null && result1.match !== 'h') {
      rules.push({ from: c, to: result1.match });
    } else {
      const result2 = closestConsonant(c, outConsonants);
      if (result2 === null) {
        unhandled.push(c);
      } else {
        rules.push({ from: c, to: result2 });
      }
    }
  });

  return { rules, common, unhandled };
}
